import { Add, Close } from '@mui/icons-material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import MonitorRoundedIcon from '@mui/icons-material/MonitorRounded';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const SEVERITY_LEVELS = [
  'Information',
  'Trivial',
  'Minor',
  'Medium',
  'Major',
  'Critical',
];

const SEVERITY_COLORS = {
  Critical: '#FF3838',
  Major: '#FF3838',
  Medium: '#FFB302',
  Minor: '#FCE83A',
  Trivial: '#56F000',
  Information: '#2DCCFF',
  Unknown: 'transparent',
};

const CreateAlarmGroupDialog = ({
  selectedAccount,
  open,
  onClose,
  onCreate,
  userOptions,
  alarmTypeOptions,
}) => {
  const [groupName, setGroupName] = useState('');
  const [users, setUsers] = useState([
    { user: '', feedSeverity: '', emailSeverity: '' },
  ]);
  const [alarmTypes, setAlarmTypes] = useState([]);

  const resetForm = () => {
    setGroupName('');
    setUsers([{ user: '', feedSeverity: '', emailSeverity: '' }]);
    setAlarmTypes([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddUser = () => {
    setUsers([...users, { user: '', feedSeverity: '', emailSeverity: '' }]);
  };

  const handleUserChange = (index, field, value) => {
    const updatedUsers = [...users];
    updatedUsers[index][field] = value;
    setUsers(updatedUsers);
  };

  const handleRemoveUser = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const handleCreate = () => {
    const Name = groupName;

    // Get user subscription details
    const AlarmSubscriptions = users
      .filter((u) => !!u.user)
      .map((u) => {
        const matchedUser = userOptions.find(
          (opt) => opt.User.Email === u.user,
        );
        return {
          User: { UserId: matchedUser?.User?.UserId ?? 0 },
          FeedSeverity: u.feedSeverity || 'Information',
          EmailSeverity: u.emailSeverity || 'Information',
        };
      });

    // Invert selected types to form AlarmExclusions
    const selectedIds = new Set(alarmTypes); // selected = to include
    const AlarmExclusions = alarmTypeOptions
      .filter((type) => !selectedIds.has(type.AlarmTypeId)) // exclude the rest
      .map((type) => ({
        AlarmType: { AlarmTypeId: type.AlarmTypeId },
      }));

    const payload = {
      Name,
      Account: { AccountId: selectedAccount.AccountId }, // Replace with real account ID
      AlarmSubscriptions,
      AlarmExclusions,
    };

    // send to API or close dialog
    onCreate(payload);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? '#000' : '#fff',
          border: '1px solid',
          borderColor: (theme) => theme.palette.divider,
          borderRadius: '8px',
        },
      }}
    >
      <DialogTitle>
        Create New Alarm Group
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={3}>
          <TextField
            fullWidth
            label="Group Name"
            size="small"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
        </Box>

        <Box mb={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle1">Users</Typography>
            <Button
              variant="text"
              startIcon={<Add />}
              onClick={handleAddUser}
              sx={{ textTransform: 'none' }}
            >
              Add User
            </Button>
          </Box>

          {users.map((entry, index) => (
            <Grid
              container
              spacing={2}
              key={index}
              alignItems="center"
              sx={{ mb: 1, width: '100%' }}
            >
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    value={entry.user}
                    label="User"
                    onChange={(e) =>
                      handleUserChange(index, 'user', e.target.value)
                    }
                  >
                    {userOptions?.map((user) => (
                      <MenuItem key={user.User.UserId} value={user.User.Email}>
                        {user.User.Email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs>
                <FormControl fullWidth size="small">
                  <InputLabel id={`severity-label-${index}`}>
                    Feed Threshold
                  </InputLabel>
                  <Select
                    labelId={`severity-label-${index}`}
                    value={entry.feedSeverity}
                    label="Feed Severity"
                    onChange={(e) =>
                      handleUserChange(index, 'feedSeverity', e.target.value)
                    }
                    renderValue={(selected) => (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          borderLeft: '5px solid',
                          borderColor:
                            SEVERITY_COLORS[selected] || 'transparent',
                          pl: 1,
                        }}
                      >
                        <MonitorRoundedIcon fontSize="small" />
                        {selected}
                      </Box>
                    )}
                  >
                    {SEVERITY_LEVELS.map((level) => (
                      <MenuItem
                        key={level}
                        value={level}
                        sx={{
                          borderLeft: '5px solid',
                          borderColor: SEVERITY_COLORS[level],
                        }}
                      >
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs>
                <FormControl fullWidth size="small">
                  <InputLabel id={`email-severity-label-${index}`}>
                    Email Threshold
                  </InputLabel>
                  <Select
                    labelId={`email-severity-label-${index}`}
                    value={entry.emailSeverity}
                    label="Email Severity"
                    onChange={(e) =>
                      handleUserChange(index, 'emailSeverity', e.target.value)
                    }
                    renderValue={(selected) => (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          borderLeft: '5px solid',
                          borderColor:
                            SEVERITY_COLORS[selected] || 'transparent',
                          pl: 1,
                          height: '100%',
                        }}
                      >
                        <EmailRoundedIcon fontSize="small" />
                        {selected}
                      </Box>
                    )}
                  >
                    {SEVERITY_LEVELS.map((level) => (
                      <MenuItem
                        dense
                        key={level}
                        value={level}
                        sx={{
                          borderLeft: '5px solid',
                          borderColor: SEVERITY_COLORS[level],
                        }}
                      >
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={0.5}>
                <IconButton
                  onClick={() => handleRemoveUser(index)}
                  sx={{
                    p: 0.9,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Close />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Box>

        <Box>
          <Typography variant="subtitle1" mb={1}>
            Alarm Types
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Alarm Types</InputLabel>
            <Select
              multiple
              value={alarmTypes}
              onChange={(e) => setAlarmTypes(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const matched = alarmTypeOptions.find(
                      (opt) => opt.AlarmTypeId === value,
                    );
                    return (
                      <Chip
                        key={value}
                        label={matched ? matched.Name : value}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {alarmTypeOptions?.map((type) => (
                <MenuItem key={type.AlarmTypeId} value={type.AlarmTypeId}>
                  {type.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Afbryd</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!groupName || !users.length}
        >
          Opret
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAlarmGroupDialog;
